import { useEffect, useMemo, useState } from "react";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import {
  fetchTableData,
  setTableDraftFilter,
  applyTableFilters,
  setTablePagination,
  setTableSorting,
  setTableColumnOrder,
  clearTableFilters,
  saveTableState,
  resetTableStateToDefault,
  selectTableDraftFilters,
  selectTableAppliedFilters,
  selectTablePagination,
  selectTableSorting,
  selectTableColumnOrder,
  selectTableListData,
  selectIsTableFilterApplied,
  selectHasTemporaryChanges,
  selectTableState,
  selectIsDefaultPreference, // NEW IMPORT
} from "../../redux/preferenceSlice";
import defaultTableConfig from "../../shared/tableConfig.json";
import ASCButton from "../../components/Button";
import ASCDropDown from "../../components/DropDown";
import Search from "../../components/Search";
import DropDownBox from "../../components/DropDownBox";
import { checkUserPermission, convertStringToArray } from "../../utils";
import { UserPermissionsList } from "../../config";
import PermissionGuard from "../../components/PermissionGuard";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Toaster,
  Tooltip,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from "@fluentui/react-components";
import {
  FilterDismiss20Regular,
  ArrowClockwise20Regular,
  ArrowSwap20Regular,
} from "@fluentui/react-icons";
import { useToastMessage } from "../../components/ToastMessage/ToastMessage";
import {
  Eye16Filled,
  Edit16Filled,
  MoreHorizontal24Filled,
} from "@fluentui/react-icons";
import apiServices from "../../service";
import { ClientRow } from "../../types/client";
import SendMail from "./SendMail";
import { setClientValue, setPartnerIDs } from "../../redux/clientSlice";
import ModalDialog from "../../components/ModalDialog";
import MainPage from "../ClientPartnerSplit/MainPage";
import SavedSearchDialog from "./SavedSearches";
import { ArrowDownload20Filled } from "@fluentui/react-icons";
import ExportMain from "../Export/ExportMain";
import { fetchExtracts, selectConditionExtracts, selectSelectionExtracts } from "../../redux/savedSearchSlices/extractSlice";
import { fetchSettings } from "../../redux/formSlice";
import { isEqual } from "lodash";

const TABLE_NAME = "CLIENT_DETAILS";

const scopeData = [
  { value: "client", label: "Client" },
  { value: "partner", label: "Partner" },
];

function List() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const { showToast, toasterId } = useToastMessage();

  /* ================= USESTATE ================= */
  const [isSendMailDialogOpen, setSendMailDialogOpen] = useState(false);
  const [selectClient, setSelectClient] = useState<{
    customer_id: string;
    first_names: string;
    last_name: string;
  }>({ customer_id: "", first_names: "", last_name: "" });
  const [openSplit, setOpenSplit] = useState<boolean>(false);
  const [ClientPartner, setClientPartner] = useState<any>(null);
  const [partnerID, setPartnerID] = useState("");
  const [openSavedSearch, setOpenSavedSearch] = useState(false);
  const [selectedSavedSelection, setSelectedSavedSelection] = useState<
    any
  >(null);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const handleOpen = () => setOpenExportDialog(true);
  const handleClose = () => setOpenExportDialog(false);
  const conditionExtracts = useSelector(selectConditionExtracts);
  const selectionExtracts = useSelector(selectSelectionExtracts);
  const dialogModalOptions = useSelector((state: RootState) => state.dialog);
  const loggedUser = useSelector((state: RootState) => state.authUser);
  const { preferences, status: preferencesStatus } = useSelector((state: RootState) => state.preferences);
  const formSettingsObj = useSelector((state: RootState) => state.formSettings);

  // Table-specific selectors - these persist across redirects (temporary in Redux)
  const draftFilters = useSelector(selectTableDraftFilters(TABLE_NAME));
  const appliedFilters = useSelector(selectTableAppliedFilters(TABLE_NAME));
  const pagination = useSelector(selectTablePagination(TABLE_NAME));
  const sorting = useSelector(selectTableSorting(TABLE_NAME));
  const columnOrder = useSelector(selectTableColumnOrder(TABLE_NAME));
  const listData = useSelector(selectTableListData(TABLE_NAME));
  const isFilterApplied = useSelector(selectIsTableFilterApplied(TABLE_NAME));
  const { isInitialized } = useSelector((state: RootState) => selectTableState(TABLE_NAME)(state));
  const isDefaultPreference = useSelector(
    selectIsDefaultPreference(TABLE_NAME)
  );
  const hasTemporaryChanges = useSelector(
    selectHasTemporaryChanges(TABLE_NAME),
  );

  /* ================= LOCAL UI STATE (for input fields) ================= */
  const [localDraftFilters, setLocalDraftFilters] = useState({
    search: draftFilters.search || "",
    scope: draftFilters.scope || [],
    clientTypes: draftFilters.client_type || [],
    search_id: draftFilters.search_id || "",
  });

  /* ================= TABLE CONFIG ================= */
  const tableConfig =
    preferences.tables.contextSpecific[TABLE_NAME] ??
    defaultTableConfig.tables.contextSpecific[TABLE_NAME];

  const columns = useMemo<MRT_ColumnDef<ClientRow>[]>(() => {
    return (tableConfig.columns ?? []) as MRT_ColumnDef<ClientRow>[];
  }, [tableConfig]);


  useEffect(() => {
    setLocalDraftFilters({
      search: draftFilters.search || "",
      scope: draftFilters.scope || [],
      clientTypes: draftFilters.client_type || [],
      search_id: draftFilters.search_id || "",
    });
  }, [
    draftFilters.search,
    draftFilters.scope,
    draftFilters.client_type,
    draftFilters.search_id,
  ]);
  useEffect(() => {
    if (conditionExtracts.length === 0 && selectionExtracts.length === 0) {
      dispatch(fetchExtracts());
    }
  }, []);

  // Fetch data when appliedFilters or pagination changes
  useEffect(() => {
    if (!isInitialized) return;
    dispatch(
      fetchTableData({
        tableName: TABLE_NAME,
        params: {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
        },
      }),
    );
  }, [
    dispatch,
    isInitialized,
    pagination.pageIndex,
    pagination.pageSize,
    appliedFilters.search,
    appliedFilters.scope,
    appliedFilters.client_type,
    appliedFilters.search_id,
    appliedFilters.selection_ids
  ]);
  useEffect(() => {
    if (
      formSettingsObj &&
      formSettingsObj.data &&
      Object.keys(formSettingsObj.data).length === 0
    ) {
      dispatch(fetchSettings());
    }
  }, [formSettingsObj]);
  /* ================= HANDLERS ================= */
  const handleApplyFilter = () => {
    // Update Redux draft filters
    dispatch(
      setTableDraftFilter({
        tableName: TABLE_NAME,
        filter: {
          search: localDraftFilters.search,
          scope: localDraftFilters.scope,
          client_type: localDraftFilters.clientTypes,
          search_id: localDraftFilters.search_id,
          selection_ids: draftFilters.selection_ids,
        },
      }),
    );

    // Apply filters (commits draft to applied in Redux - TEMPORARY, not saved to API)
    dispatch(applyTableFilters({ tableName: TABLE_NAME }));
  };

  const savedConfig = useMemo(
    () => preferences.tables.contextSpecific[TABLE_NAME],
    [preferences.tables.contextSpecific, TABLE_NAME]
  );
  const handleClearFilter = () => {
    dispatch(clearTableFilters({ tableName: TABLE_NAME }));

    if (savedConfig?.filter) {
      setLocalDraftFilters({
        search: savedConfig.filter.search || "",
        scope: savedConfig.filter.scope || [],
        clientTypes: savedConfig.filter.client_type || [],
        search_id: savedConfig.filter.search_id || "",
      });

      setSelectedSavedSelection(savedConfig.filter.selection_id || null);
    } else {
      setLocalDraftFilters({
        search: "",
        scope: [],
        clientTypes: [],
        search_id: "",
      });

      setSelectedSavedSelection(null);
    }
  };

  const handleResetToDefault = async () => {
    try {
      // Reset saved preferences to factory defaults
      await dispatch(
        resetTableStateToDefault({ tableName: TABLE_NAME }),
      ).unwrap();

      // Clear local UI state
      setLocalDraftFilters({
        search: "",
        scope: [],
        clientTypes: [],
        search_id: "",
      });

      showToast("User preferences have been reset to default.", "", "success");
    } catch (error) {
      showToast("Failed to reset preferences", "", "error");
    }
  };

  const handleSavePreference = async () => {
    try {
      // Save current applied filters to API (makes them permanent)
      const { message } = await dispatch(
        saveTableState({
          tableName: TABLE_NAME,
          shouldRefetch: true,
        }),
      ).unwrap();

      showToast("User preferences have been updated successfully.", "", "success");
    } catch (error) {
      showToast("Failed to save preferences", "", "error");
    }
  };

  const handlePaginationChange = (
    updater:
      | MRT_PaginationState
      | ((prev: MRT_PaginationState) => MRT_PaginationState),
  ) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    dispatch(
      setTablePagination({
        tableName: TABLE_NAME,
        pagination: next,
      }),
    );
  };

  const handleSortingChange = (
    updater: MRT_SortingState | ((prev: MRT_SortingState) => MRT_SortingState),
  ) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    dispatch(
      setTableSorting({
        tableName: TABLE_NAME,
        sorting: next,
      }),
    );
  };

  const handleColumnOrderChange = (
    updater: string[] | ((prev: string[]) => string[]),
  ) => {
    const next = typeof updater === "function" ? updater(columnOrder) : updater;
    dispatch(
      setTableColumnOrder({
        tableName: TABLE_NAME,
        columnOrder: next,
      }),
    );
  };

  /* ================= UTILITY FUNCTIONS ================= */
  async function fetchClientTypeOptions(): Promise<any[]> {
    const fieldLbl = "client_type:ModifiableList";
    try {
      const res = await apiServices.selectionList.getOptions(fieldLbl);
      if (res.data && res.data.data && res.data.data.length > 0) {
        return res.data.data[0].options;
      }
      return [];
    } catch (error) {
      console.error("Error fetching client type options:", error);
      return [];
    }
  }

  function exportApi(customerId: any) {
    apiServices.exportToExcel
      .get(customerId)
      .then(async (response) => {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ClientData.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  const openSplitComponent = (customer_id: any) => {
    setOpenSplit(true);
    dispatch(setClientValue(customer_id));
  };

  const getClientPartner = (id: any) => {
    apiServices.ClientPartnerSplit.getClientDetail(id)
      .then((res) => {
        setPartnerID(res.data.data.NameAndAddress.associated_with);
        dispatch(setPartnerIDs(res.data.data.NameAndAddress.associated_with));
        setClientPartner(res.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const openSendMailDialog = (
    customer_id: string,
    first_names: string,
    last_name: string,
  ) => {
    setSendMailDialogOpen(true);
    setSelectClient({ customer_id, first_names, last_name });
  };

  const closeSendMailDialog = () => {
    setSendMailDialogOpen(false);
  };

  const handleCloseSplit = () => {
    setOpenSplit(false);
    handleClearFilter();
  };

  /* ================= MRT CONFIG ================= */
  const sortingConfig = tableConfig.sortingConfig;

  const config = useMemo(
    () => ({
      ...preferences.tables.defaultConfig,
      enablePagination: true,
      manualPagination: true,
      enableSorting: true,
      enableColumnOrdering: true,
      enableColumnDragging: true,
      initialState: {
        ...tableConfig.initialState,
        pagination,
        sorting,
        columnOrder,
        density: "compact" as const,
      },
      ...(sortingConfig?.sortableColumns && {
        columns: columns.map((col: any) => ({
          ...col,
          enableSorting: sortingConfig.sortableColumns.includes(
            col.accessorKey,
          ),
        })),
      }),
    }),
    [
      preferences.tables.defaultConfig,
      tableConfig,
      pagination,
      sorting,
      columnOrder,
      columns,
      sortingConfig,
    ],
  );

  /* ================= RENDER ================= */
  const isScopeEnabled = localDraftFilters.search.trim().length >= 3;
  const hasAnyDraftFilter =
    (localDraftFilters.search.trim() !== "" &&
      localDraftFilters.scope.length > 0) ||
    localDraftFilters.clientTypes.length > 0 ||
    localDraftFilters.search_id.trim() !== "" ||
    selectedSavedSelection
    ;
  const hasMeaningfulFilter =
    (localDraftFilters.search.trim() !== "" &&
      localDraftFilters.scope.length > 0) ||
    localDraftFilters.clientTypes.length > 0 ||
    localDraftFilters.search_id.trim() !== "" ||
    selectedSavedSelection;

  const isSameAsApplied = isEqual(localDraftFilters, {
    search: appliedFilters.search || "",
    scope: appliedFilters.scope || [],
    clientTypes: appliedFilters.client_type || [],
    search_id: appliedFilters.search_id || "",
  });

  const disableApply = !hasMeaningfulFilter || isSameAsApplied;
  const selectedTitles = useMemo(() => {
    if (draftFilters.search_id) {
      const ids = draftFilters.search_id.split(",").filter(Boolean);

      return conditionExtracts
        .filter((item: any) => ids.includes(item.SearchId))
        .map((item: any) => item.Title);
    }

    if (draftFilters.selection_id) {
      const selected = selectionExtracts.find(
        (item: any) => item.SearchId === draftFilters.selection_id
      );

      return selected ? [selected.Title] : [];
    }

    return [];
  }, [
    draftFilters.search_id,
    draftFilters.selection_id,
    conditionExtracts,
    selectionExtracts,
  ]);
  const shortTitleText = useMemo(() => {
    if (selectedTitles.length === 0) return "";

    const maxToShow = 2;
    const visible = selectedTitles.slice(0, maxToShow);
    const remaining = selectedTitles.length - maxToShow;

    return remaining > 0
      ? `${visible.join(", ")} +${remaining} more`
      : visible.join(", ");
  }, [selectedTitles]);

  const fullLabel = useMemo(() => {
    if (selectedTitles.length === 0) {
      return "Saved Searches";
    }
    else {
      return `Saved Searches - ${selectedTitles.join(", ")}`;
    }
  }, [selectedTitles]);

  const buttonLabel = selectedTitles.length
    ? `Saved Searches - ${shortTitleText}`
    : "Saved Searches";
  const hasDraftChanges = useMemo(() => {
    const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

    return (
      (localDraftFilters.search !== draftFilters.search &&
        !isEqual(localDraftFilters.scope, draftFilters.scope)) ||
      !isEqual(localDraftFilters.clientTypes, draftFilters.client_type) ||
      localDraftFilters.search_id !== draftFilters.search_id ||
      (selectedSavedSelection || null) !== (draftFilters.selection_id || null)
    );
  }, [localDraftFilters, draftFilters, selectedSavedSelection]);

  return (
    <div className="padding_20">
      <div className="flex-end">
        <PermissionGuard permission={UserPermissionsList.ALLOW_ADD_CLIENT}>
          <ASCButton
            shape="rounded"
            label="Add Client"
            appearance="primary"
            className="asc-button-primary"
            onClick={() => navigate("/clients/add")}
          />
        </PermissionGuard>
      </div>

      <div className="page-content-area">
        <Toaster toasterId={toasterId} position="top-end" timeout={5000} />

        <div className="client-list-wrapper">
          <MaterialReactTable<any>
            columns={columns}
            data={listData?.data || []}
            rowCount={listData?.total || 0}
            manualPagination
            enableRowActions
            positionActionsColumn="last"
            onPaginationChange={handlePaginationChange}
            onSortingChange={handleSortingChange}
            onColumnOrderChange={handleColumnOrderChange}
            state={{
              pagination,
              sorting,
              columnOrder,
              isLoading: listData?.status === "loading",
            }}
            muiTablePaginationProps={{
              rowsPerPageOptions: [10, 50, 100, 200, 500],
            }}
            {...config}
            muiToolbarAlertBannerProps={{ sx: { display: "none" } }}
            muiTableContainerProps={({ table }) => ({
              sx: {
                height: table.getState().isFullScreen
                  ? "calc(100vh - 90px) !important"
                  : "calc(100vh - 280px) !important",
              },
            })}
            renderTopToolbarCustomActions={() => (
              <div className="table_filter">
                <div className="global-search-filter">
                  <Search
                    search={localDraftFilters.search}
                    setSearch={(val) =>
                      setLocalDraftFilters((p: any) => ({ ...p, search: val }))
                    }
                    placeholder="Search by first or last name"
                    width="260px"
                  />

                  <DropDownBox
                    multiselect
                    disabled={!isScopeEnabled}
                    values={scopeData}
                    selectedOptions={localDraftFilters.scope}
                    placeholder={
                      localDraftFilters?.scope?.length
                        ? localDraftFilters.scope.join(", ")
                        : "Scope"
                    }
                    onOptionSelect={(_, option) =>
                      setLocalDraftFilters((p) => ({
                        ...p,
                        scope: option.selectedOptions,
                      }))
                    }
                  />

                  <ASCDropDown
                    multiSelect
                    selectedValues={localDraftFilters.clientTypes}
                    placeholder={
                      localDraftFilters.clientTypes.length
                        ? localDraftFilters.clientTypes.join(", ")
                        : "Client Type"
                    }
                    optionsFetcher={fetchClientTypeOptions}
                    onSelectionChange={(val: string[]) =>
                      setLocalDraftFilters((p) => ({
                        ...p,
                        clientTypes: val,
                      }))
                    }
                    style={{ minWidth: "min-content" }}
                  />
                  {!convertStringToArray(
                    process.env.REACT_APP_HIDE_CTA_BUTTON,
                  ).includes("saved_searches") ? (
                    <Tooltip content={fullLabel} relationship="label" positioning="below" >
                      <Button
                        shape="rounded"
                        onClick={() => setOpenSavedSearch(true)}
                      >{buttonLabel}</Button>
                    </Tooltip>
                  ) : (
                    ""
                  )}

                  <ASCButton
                    shape="rounded"
                    label="Apply"
                    appearance="primary"
                    className="asc-button-primary"
                    disabled={disableApply}
                    onClick={handleApplyFilter}
                  />
                  {(hasTemporaryChanges) && (
                    <Tooltip content="Clear filter" relationship="label" positioning="below">
                      <Button
                        icon={<FilterDismiss20Regular />}
                        onClick={handleClearFilter}
                      />
                    </Tooltip>
                  )}
                  {!convertStringToArray(
                    process.env.REACT_APP_HIDE_CTA_BUTTON,
                  ).includes("client_list_preference") && (
                      <>
                        <ASCButton
                          label="Save Preference"
                          shape="rounded"
                          appearance="secondary"
                          onClick={handleSavePreference}
                          disabled={!isDefaultPreference || !hasTemporaryChanges}
                        />
                        <Tooltip content="Reset to Default" relationship="label" positioning="below">
                          <Button
                            icon={<ArrowClockwise20Regular />}
                            onClick={handleResetToDefault}
                            disabled={isDefaultPreference}
                          />
                        </Tooltip>


                      </>
                    )}
                </div>

                <div style={{ display: "flex", gap: "10px" }}>

                  {!convertStringToArray(
                    process.env.REACT_APP_HIDE_CTA_BUTTON,
                  ).includes("saved_searches") && (
                      <Tooltip
                        content="Export"
                        relationship="label" positioning="below"
                        withArrow
                      >
                        {(appliedFilters?.search_id ||
                          appliedFilters?.selection_ids?.length > 0) && (
                            <Button
                              icon={<ArrowDownload20Filled />}
                              onClick={handleOpen}
                              disabled={
                                !appliedFilters?.search_id &&
                                (!appliedFilters?.selection_ids ||
                                  appliedFilters.selection_ids.length === 0)
                              }
                            />
                          )}
                      </Tooltip>
                    )}
                </div>
              </div>
            )}
            renderRowActions={({ row }) => (
              <div>

                <PermissionGuard permission={UserPermissionsList.ALLOW_VIEW_CLIENT}>
                  <Tooltip
                    content="View"
                    relationship="label" positioning="below"
                  >
                    <Button
                      shape="rounded"
                      appearance="subtle"
                      onClick={() => {
                        navigate(
                          `clients/${row?.original?.associated_type == 1
                            ? row.original.associated_with
                            : row.original.customer_id
                          }/name-and-address/view`,
                        );
                      }}
                      icon={<Eye16Filled primaryFill="var(--button-color)" />}
                    />
                  </Tooltip>
                </PermissionGuard>
                <PermissionGuard permission={UserPermissionsList.ALLOW_UPDATE_CLIENT}>
                  <Tooltip
                    content="Edit"
                    relationship="label" positioning="below"
                  >
                    <Button
                      shape="rounded"
                      appearance="subtle"
                      onClick={() => {
                        navigate(
                          `clients/${row.original.associated_type == 1
                            ? row.original.associated_with
                            : row.original.customer_id
                          }/name-and-address/edit`,
                        );
                      }}
                      icon={<Edit16Filled primaryFill="var(--button-color)" />}
                    />
                  </Tooltip>
                </PermissionGuard>
                {(checkUserPermission(
                  loggedUser,
                  UserPermissionsList.ALLOW_CLIENT_EXPORTS,
                ) ||
                  checkUserPermission(
                    loggedUser,
                    UserPermissionsList.ALLOW_UPDATE_CLIENT,
                  )) && (
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button
                          shape="rounded"
                          appearance="subtle"
                          icon={<MoreHorizontal24Filled primaryFill="gray" />}
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <PermissionGuard permission={UserPermissionsList.ALLOW_CLIENT_EXPORTS}>
                            <MenuItem onClick={() => exportApi(row.original.customer_id)}>
                              Export to Excel
                            </MenuItem>
                          </PermissionGuard>
                          <MenuItem
                            disabled={!row.original.email}
                            onClick={() =>
                              openSendMailDialog(
                                row.original.customer_id,
                                row.original.first_names,
                                row.original.last_name,
                              )
                            }
                          >
                            Send Mail
                          </MenuItem>
                          {!convertStringToArray(
                            process.env.REACT_APP_HIDE_CTA_BUTTON,
                          ).includes("client_partner_split") ? (
                            <MenuItem
                              onClick={() => {
                                openSplitComponent(row.original.customer_id);
                                getClientPartner(row.original.customer_id);
                              }}
                              disabled={row.original.associated_with === null}
                            >
                              Split
                            </MenuItem>
                          ) : (
                            ""
                          )}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  )}
              </div>
            )}
            icons={{
              DragHandleIcon: () => (
                <ArrowSwap20Regular style={{ color: "000", cursor: "grab" }} />
              ),
            }}
          />
        </div>
      </div>

      {isSendMailDialogOpen && (
        <SendMail
          openSendMailDialog={openSendMailDialog}
          isSendMailDialogOpen={isSendMailDialogOpen}
          closeSendMailDialog={closeSendMailDialog}
          selectClient={selectClient}
          notify={showToast}
        />
      )}

      <ModalDialog options={dialogModalOptions} />

      <MainPage
        isOpen={openSplit}
        ClientPartner={ClientPartner}
        onClose={handleCloseSplit}
        partnerID={partnerID}
      />
      <SavedSearchDialog
        open={openSavedSearch}
        onClose={() => setOpenSavedSearch(false)}
        initialConditionIds={useMemo(() =>
          draftFilters.search_id ? draftFilters.search_id.split(",") : []
          , [draftFilters.search_id])}
        initialSelectionId={selectedSavedSelection}
        onApply={({ conditionIds, selectionIds, selectionId }) => {

          dispatch(
            setTableDraftFilter({
              tableName: TABLE_NAME,
              filter: {
                search_id: conditionIds.join(","),     // condition mode
                selection_ids: selectionIds || [],     // ✅ correct
                selection_id: selectionId,
              },
            }),
          );

          // ✅ store actual selectionId
          setSelectedSavedSelection(selectionId);
        }}
      />
      {(appliedFilters?.search_id || appliedFilters?.selection_ids) && (
        <ExportMain
          isOpen={openExportDialog}
          onClose={handleClose}
          appliedFilters={appliedFilters}
        />
      )}
    </div>
  );
}

export default List;
